import type { NotificationDto } from '@talentbabu/types';
import { cn } from '@talentbabu/ui';
import { Bell, CheckCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuthStore } from '../../app/auth-store';
import { useAreaBase } from '../../lib/area';
import { formatRelative } from '../../lib/format';
import { localizeNotificationUrl, supportTicketUrl, useMarkRead, useNotifications, useUnreadCount } from './api';

export function NotificationItem({ n, onOpen }: { n: NotificationDto; onOpen: (n: NotificationDto) => void }) {
  const scheduled = n.type === 'INTERVIEW' && n.title === 'Interview scheduled';
  // The message ends with the interview date and time; keep it bold whether or not it is read.
  const cut = scheduled ? n.message.lastIndexOf(' · ') : -1;
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className={cn(
        'flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50 cursor-pointer',
        !n.read && 'bg-brand-50/40',
      )}
    >
      <span
        className={cn(
          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
          n.read ? 'bg-transparent' : scheduled ? 'bg-green-500' : 'bg-brand-500',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">{n.title}</span>
        <span className="block text-sm text-slate-600">
          {cut > 0 ? (
            <>
              {n.message.slice(0, cut + 3)}
              <strong className="font-bold">{n.message.slice(cut + 3)}</strong>
            </>
          ) : (
            n.message
          )}
        </span>
        <span className="mt-0.5 block text-xs text-slate-400">{formatRelative(n.createdAt)}</span>
      </span>
    </button>
  );
}

export function useOpenNotification() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const markRead = useMarkRead();
  return (n: NotificationDto) => {
    if (!n.read) markRead.mutate(n.id);
    const url = supportTicketUrl(n, role) ?? localizeNotificationUrl(n.url, role);
    if (url) navigate(url);
  };
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const base = useAreaBase();
  const { data: count = 0 } = useUnreadCount();
  const { data } = useNotifications(1, 8);
  const markRead = useMarkRead();
  const openNotification = useOpenNotification();

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 cursor-pointer"
        aria-label={`Notifications${count ? `, ${count} unread` : ''}`}
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[var(--shadow-raised)]">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="font-semibold text-slate-900">Notifications</p>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate('all')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 cursor-pointer"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {!data?.items.length ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">You’re all caught up.</p>
            ) : (
              data.items.map((n) => (
                <NotificationItem
                  key={n.id}
                  n={n}
                  onOpen={(item) => {
                    setOpen(false);
                    openNotification(item);
                  }}
                />
              ))
            )}
          </div>
          <Link
            to={`${base}/notifications`}
            onClick={() => setOpen(false)}
            className="block border-t border-slate-100 py-2.5 text-center text-sm font-semibold text-brand-600 hover:bg-slate-50"
          >
            See all
          </Link>
        </div>
      )}
    </div>
  );
}
